import { useEffect, useState } from "react";
import type { Account, EntityExternalLink } from "@sales-ops-amplify/shared";
import { useUpdateAccount } from "@/hooks/useCompanies";
import {
  ExternalLinksEditor,
  sanitizeExternalLinks,
} from "@/components/external-links-editor";

function listFieldToCsv(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(String).map((s) => s.trim()).filter(Boolean).join(", ");
  }
  if (typeof value === "string") return value;
  return "";
}

export function AccountEditForm({
  account,
  accountId,
  onSaved,
  onCancel,
}: {
  account: Account;
  accountId: string;
  onSaved?: () => void;
  onCancel?: () => void;
}) {
  const updateAccount = useUpdateAccount(accountId);

  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("normal");
  const [tagsInput, setTagsInput] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountUrl, setAccountUrl] = useState("");
  const [accountDomain, setAccountDomain] = useState("");
  const [accountIndustry, setAccountIndustry] = useState("");
  const [accountDescription, setAccountDescription] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [technologies, setTechnologies] = useState("");
  const [funding, setFunding] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [lifecycleStage, setLifecycleStage] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [signalsUrlInput, setSignalsUrlInput] = useState("");
  const [addrStreet, setAddrStreet] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [addrCountry, setAddrCountry] = useState("");
  const [ats, setAts] = useState("");
  const [boardToken, setBoardToken] = useState("");
  const [scanEnabled, setScanEnabled] = useState(false);
  const [externalLinks, setExternalLinks] = useState<EntityExternalLink[]>([]);

  useEffect(() => {
    setNotes(account.notes ?? "");
    setStatus(account.status ?? "");
    setPriority(account.priority ?? "normal");
    setTagsInput(listFieldToCsv(account.tags));
    setAccountName(account.name ?? "");
    setAccountUrl(account.url ?? "");
    setAccountDomain(account.domain ?? "");
    setAccountIndustry(account.industry ?? "");
    setAccountDescription(account.description ?? "");
    setEmployeeCount(
      account.employeeCount != null ? String(account.employeeCount) : "",
    );
    setTechnologies(listFieldToCsv(account.technologies));
    setFunding(account.funding ?? "");
    setPhone(account.phone ?? "");
    setLinkedinUrl(account.linkedinUrl ?? "");
    setLifecycleStage(account.lifecycleStage ?? "");
    setOwnerName(account.ownerName ?? "");
    setSignalsUrlInput(account.signalsUrl ?? "");
    setAddrStreet(account.address?.street ?? "");
    setAddrCity(account.address?.city ?? "");
    setAddrState(account.address?.state ?? "");
    setAddrPostal(account.address?.postalCode ?? "");
    setAddrCountry(account.address?.country ?? "");
    setAts(account.ats ?? "");
    setBoardToken(account.boardToken ?? "");
    setScanEnabled(Boolean(account.scanEnabled));
    setExternalLinks(
      account.externalLinks?.length
        ? account.externalLinks.map((l) => ({ ...l }))
        : [],
    );
  }, [account]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const tech = technologies
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const emp = employeeCount.trim() ? Number(employeeCount) : undefined;
    await updateAccount.mutateAsync({
      name: accountName.trim() || undefined,
      status: status || undefined,
      priority: priority || undefined,
      tags,
      url: accountUrl.trim() || undefined,
      domain: accountDomain.trim() || undefined,
      industry: accountIndustry.trim() || undefined,
      description: accountDescription.trim() || undefined,
      employeeCount: Number.isFinite(emp) ? emp : undefined,
      technologies: tech,
      funding: funding.trim() || undefined,
      phone: phone.trim() || undefined,
      linkedinUrl: linkedinUrl.trim() || undefined,
      lifecycleStage: lifecycleStage.trim() || undefined,
      ownerName: ownerName.trim() || undefined,
      signalsUrl: signalsUrlInput.trim() || undefined,
      address: {
        street: addrStreet.trim() || undefined,
        city: addrCity.trim() || undefined,
        state: addrState.trim() || undefined,
        postalCode: addrPostal.trim() || undefined,
        country: addrCountry.trim() || undefined,
      },
      ats: ats.trim() || undefined,
      boardToken: boardToken.trim() || undefined,
      scanEnabled,
      externalLinks: sanitizeExternalLinks(externalLinks),
      notes: notes.trim() || undefined,
    });
    onSaved?.();
  }

  const inputClass =
    "mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-xs text-muted-foreground">
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={inputClass}
          >
            <option value="">Unset</option>
            <option value="watching">Watching</option>
            <option value="engaged">Engaged</option>
            <option value="proposal">Proposal</option>
            <option value="negotiation">Negotiation</option>
            <option value="customer">Customer</option>
            <option value="passed">Passed</option>
          </select>
        </label>
        <label className="block text-xs text-muted-foreground">
          Priority
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className={inputClass}
          >
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label className="block text-xs text-muted-foreground">
          Tags (comma-separated)
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="enterprise, warming, partner"
            className={inputClass}
          />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs text-muted-foreground">
          Account name
          <input
            type="text"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Industry
          <input
            type="text"
            value={accountIndustry}
            onChange={(e) => setAccountIndustry(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Website URL
          <input
            type="url"
            value={accountUrl}
            onChange={(e) => setAccountUrl(e.target.value)}
            placeholder="https://"
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Domain
          <input
            type="text"
            value={accountDomain}
            onChange={(e) => setAccountDomain(e.target.value)}
            placeholder="example.com"
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground sm:col-span-2">
          Description
          <textarea
            value={accountDescription}
            onChange={(e) => setAccountDescription(e.target.value)}
            rows={2}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Employee count
          <input
            type="number"
            min={0}
            value={employeeCount}
            onChange={(e) => setEmployeeCount(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Funding
          <input
            type="text"
            value={funding}
            onChange={(e) => setFunding(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground sm:col-span-2">
          Technologies (comma-separated)
          <input
            type="text"
            value={technologies}
            onChange={(e) => setTechnologies(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Phone
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          LinkedIn URL
          <input
            type="url"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Lifecycle stage
          <input
            type="text"
            value={lifecycleStage}
            onChange={(e) => setLifecycleStage(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Owner
          <input
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground sm:col-span-2">
          Signals URL
          <input
            type="url"
            value={signalsUrlInput}
            onChange={(e) => setSignalsUrlInput(e.target.value)}
            className={inputClass}
          />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs text-muted-foreground sm:col-span-2">
          Street
          <input
            type="text"
            value={addrStreet}
            onChange={(e) => setAddrStreet(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          City
          <input
            type="text"
            value={addrCity}
            onChange={(e) => setAddrCity(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          State
          <input
            type="text"
            value={addrState}
            onChange={(e) => setAddrState(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Postal code
          <input
            type="text"
            value={addrPostal}
            onChange={(e) => setAddrPostal(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Country
          <input
            type="text"
            value={addrCountry}
            onChange={(e) => setAddrCountry(e.target.value)}
            className={inputClass}
          />
        </label>
      </div>

      <label className="block text-xs text-muted-foreground">
        Notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className={inputClass}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-xs text-muted-foreground">
          ATS
          <input
            type="text"
            value={ats}
            onChange={(e) => setAts(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Board token
          <input
            type="text"
            value={boardToken}
            onChange={(e) => setBoardToken(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="flex items-center gap-2 pt-6 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={scanEnabled}
            onChange={(e) => setScanEnabled(e.target.checked)}
            className="rounded border-border"
          />
          Scan enabled
        </label>
      </div>

      <ExternalLinksEditor
        value={externalLinks}
        onChange={setExternalLinks}
        disabled={updateAccount.isPending}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={updateAccount.isPending}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {updateAccount.isPending ? "Saving…" : "Save changes"}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
